// Platform settings. Each value is a starting point to tune; the Design
// pages record where it came from.

export const MAX_WORDS = 25;
// A cap on characters too, so a few very long "words" can't make pattern
// scanning slow. 25 words average under 150 characters.
export const MAX_CHARS = 300;
// A refund's reason is stored in D1 as given, so it has a cap too.
export const MAX_REASON_CHARS = 500;
export const MAX_ANSWERS = 3;
// A request's questions' maxAnswers add up to at most this, so a request
// dispatches at most this many hubs, and makes at most this many router model
// calls. CONSUMER_LIMITER (wrangler.jsonc) times this stays under the AI
// Gateway's 600 calls a minute: 120 x 4 = 480. Each question's maxAnswers is
// at least 1, so it caps questions too. See Design > Exchange, "Rate limit".
export const MAX_ANSWERS_PER_REQUEST = 4;
export const MAX_QUESTIONS = MAX_ANSWERS_PER_REQUEST;

// Best vector distance above which a hub is dropped, when hub selection
// falls back to the cutoff. From the turbopuffer test with
// voyage/voyage-4-lite: in-scope questions reached at most 0.611, and this
// value declined 4 of 8 out-of-scope questions with no in-scope losses.
export const DISTANCE_CUTOFF = 0.62;

// A hub cut at the cutoff counts as a near miss, and gets a demand event,
// when its distance is within this margin above the cutoff. At 0.1, "Which
// geography has GEOID 37191?" (0.642 to us.geography) is a near miss. So is
// "What is Taylor Swift's first album?" for us.geography (0.699), but not
// for us.infrastructure (0.761).
export const NEAR_MISS_MARGIN = 0.1;

// Hub selection considers the top this-many distinct hub:intent matches. A
// hub's candidate intents are its intents among them. See Design >
// HubSelector, "Hub selection".
export const SELECTION_CANDIDATES = 10;

// Dispatch thresholds, the default egress allowlist, the answer collection
// limit and the staging caps live in the Tool SDK, which checks them too.
export {
  API_ACCOUNTS_PER_HOST, DEFAULT_DISPATCH_THRESHOLD, DEFAULT_EGRESS_ALLOW, DISPATCH_THRESHOLD_FLOOR, MAX_BUNDLE_BYTES, MAX_COLLECTION_MEMBERS, MAX_EXAMPLES_PER_INTENT,
  MAX_GOLDEN_QUESTIONS, MAX_INTENTS, MAX_MANIFEST_BYTES, MAX_TOOLSET_BUNDLE_BYTES,
} from "./tool-sdk/limits.ts";

// Hub scores come from Jev's System One API. A request that fails or takes
// longer than the timeout falls back to the distance cutoff.
export const HUB_SCORE_URL = "https://api.typesafe.ai/v1/systemone";
export const HUB_SCORE_MODEL = "jev-latest";
export const HUB_SCORE_TIMEOUT_MS = 1000;

// How long KV keeps a hub score. jev-latest moves to new model versions, so
// entries expire to pick them up.
export const HUB_SCORE_CACHE_TTL_SECONDS = 86_400;

// Longest a WarmHub HTTP call may take. Reads normally take 76-500 ms, and
// a cold prefix query took 1,528 ms (October 3, 2026). On October 4 at
// 01:41 UTC, three slow calls held one question for 16.7 seconds. A call
// past this limit fails as a retryable WarmHubError.
export const WARMHUB_TIMEOUT_MS = 4000;
// How long after WARMHUB_TIMEOUT_MS a WarmHub fetch is aborted. The SDK's
// reads take no AbortSignal, so the call's deadline is Effect.timeout, and
// this abort, which only frees the connection, comes after it, so the
// deadline always fires first.
export const WARMHUB_ABORT_GRACE_MS = 1000;

// The most WarmHub calls one hub's dispatch has in flight at once, across
// pattern hints, parameter resolution and answer validation, however their
// fan-outs nest: each call holds a permit of its HubDispatch's semaphore. A
// Worker invocation has at most six connections waiting for response headers
// at once, and a seventh queues
// (https://developers.cloudflare.com/workers/platform/limits/#simultaneous-open-connections),
// so more would only queue. See Design > HubRouter, "Parameter resolution".
export const WARMHUB_READS_AT_ONCE = 6;

// Timeouts for the platform's other outbound calls, and a deadline for one
// hub dispatch: hints, intent routing, resolution, and the tool. A call or a
// dispatch that runs past its limit fails as retryable.
// turbopuffer on dev, October 9-15, 2026: 610 queries, p50 144 ms, p99 510 ms,
// max 673 ms.
export const INDEX_TIMEOUT_MS = 3000;
// A write to the intent index, which embeds each row's text: activation and
// test runs wait on it.
export const INDEX_WRITE_TIMEOUT_MS = 15_000;
// The router model on dev, October 9-15, 2026: a gateway cache miss had p50
// 2,751 ms and p99 6,674 ms over 219 calls; on October 15 alone, p99 was
// 9,350 ms over 75. A hit had p99 1,758 ms over 556. 14 s is 1.5 times that
// day's p99, and leaves room within DISPATCH_DEADLINE_MS for the rest of the
// answer path (p99 1.9 s). See Design > HubRouter, "Intent routing".
export const ROUTER_TIMEOUT_MS = 14_000;
export const DISPATCH_DEADLINE_MS = 20_000; // One hub's whole answer path

// How long an isolate reuses each listed hub's active toolset and the hub
// records it read from D1. A newly activated toolset reaches every isolate
// within this time. See Design > HubRouter, "Toolsets".
export const TOOLSETS_TTL_MS = 30_000;
// A linked repository's visibility, as the audience check reads it, per
// isolate and hub. Under VISIBILITY_FRESH_MS old it's used as read; until
// VISIBILITY_MAX_AGE_MS a request uses it and starts one refresh after the
// response; older, the request reads it itself. So a linked repository that
// turns public stops admitting anyone within VISIBILITY_MAX_AGE_MS, the
// bound the curator view cache sets for a reader losing repo:read. See
// Design > Operations, "Caches".
export const VISIBILITY_FRESH_MS = 30_000;
export const VISIBILITY_MAX_AGE_MS = 60_000;
// The longest an isolate's shared toolset or hub record read may take, and
// the longest a request waits for another request's read before it reads
// itself. See Design > HubRouter, "Toolsets".
export const TOOLSETS_READ_TIMEOUT_MS = 5000;

// The longest the Curator API waits for one call to a hub's HubAgent Durable
// Object: activity(), transcript() or reply(). Each reads or writes the
// object's own SQLite, a few local statements, and reply() delivers to its
// Hub inbox and sets its alarm: milliseconds once the object runs. The wait
// is the rest: a cold start that runs the tables' init and migrations under
// blockConcurrencyWhile, the object's queue while an alarm's agent action
// holds it between awaits, and a long transcript's entry pages. No deployed
// latency is measured yet; 10 s covers those with room, and stays well under
// what a curator waits for before giving up. A call past it is a retryable
// StoreError with timedOut, a 503, as other upstream timeouts are. A reply
// that times out may still have been kept: Studio reads the activity again
// after any reply, so a kept one shows. See Design > Exchange, "The hub
// agent's activity".
export const HUB_AGENT_TIMEOUT_MS = 10_000;

// Staging (see Design > Toolsets and tools, "Staging checks"): the Tool SDK
// versions it accepts (#43; the staging caps, such as MAX_BUNDLE_BYTES, are
// re-exported from the Tool SDK's limits above), how long a
// toolset may stay validating before it reads as rejected (longer than the
// StagingWorkflow's steps and their retries, about 17 minutes), and how long one
// bundle's describe() may take, a cold load from Artifacts included (384-672
// ms in the spike).
export const SUPPORTED_SDK_VERSIONS: readonly string[] = ["0.1.0"];
export const STAGING_TIMEOUT_MS = 20 * 60_000;
export const STAGING_DESCRIBE_MS = 10_000;

// Edge cache time for tool bundles read from KV. A blob SHA always names the
// same bundle, so this only bounds memory, not staleness.
// The EgressGateway's limits on each request.
export const EGRESS_TIMEOUT_MS = 5000;
// Redirects the EgressGateway follows, each checked against the allowlist.
export const EGRESS_MAX_REDIRECTS = 5;
export const EGRESS_MAX_BYTES = 25 * 1024 * 1024;
// The API keys a hub may hold for one account of one host, one per header
// or query parameter: an API that takes two, such as an application's ID
// and its key, fits with room to spare. See Design > Exchange, "Curator API".
export const API_KEYS_PER_ACCOUNT = 4;
// A grant credential's API key token: the EgressGateway uses a stored one
// while it has more than API_KEY_TOKEN_RENEW_WITHIN_MS left, and otherwise
// renews it with a token call that gets API_KEY_TOKEN_TIMEOUT_MS of the
// request's EGRESS_TIMEOUT_MS; it takes a token whose expiry is in the
// future and no more than API_KEY_TOKEN_MAX_LIFETIME_MS away, from a body of
// at most API_KEY_TOKEN_MAX_BYTES. A renewal's claim lasts at most
// API_KEY_RENEWAL_CLAIM_MS, and a request that waits for another's renewal
// looks for its token every API_KEY_TOKEN_POLL_MS for up to
// API_KEY_TOKEN_TIMEOUT_MS. See Design > Toolsets and tools, "EgressGateway".
export const API_KEY_TOKEN_RENEW_WITHIN_MS = 10 * 60_000;
export const API_KEY_TOKEN_MAX_LIFETIME_MS = 7 * 86_400_000;
export const API_KEY_TOKEN_MAX_BYTES = 8 * 1024;
export const API_KEY_TOKEN_TIMEOUT_MS = 3000;
export const API_KEY_RENEWAL_CLAIM_MS = 10_000;
export const API_KEY_TOKEN_POLL_MS = 100;

// How long KV keeps a shape definition. A pinned shape version never
// changes, so this only bounds storage.
export const SHAPE_DEF_CACHE_TTL_SECONDS = 30 * 86_400;

// The most shape refs that one GET /v1/shapes request can name.
export const MAX_SHAPE_REFS = 20;

export const BUNDLE_CACHE_TTL_SECONDS = 86_400;

// Reciprocal rank fusion constant for combining vector and BM25 ranks.
export const RRF_K = 60;

// Rows requested from each intent-index subquery. The hub-selection
// experiment used 50, which leaves enough distinct hub:intent matches for
// SELECTION_CANDIDATES.
export const INDEX_ROWS_PER_QUERY = 50;


// Refund-rate prior and demonetization rule from Design > Exchange,
// "Refunds and ratings".
export const RATING_PRIOR_REFUNDS = 1;
export const RATING_PRIOR_ANSWERS = 20;
export const DEMONETIZE_MIN_ANSWERS = 50;
export const DEMONETIZE_REFUND_RATE = 0.2;
// How long each isolate reuses the hubs' answer and refund counts, so a
// request doesn't count the whole answers table. A refund changes a hub's
// rating within this time.
export const RATING_COUNTS_TTL_MS = 60_000;
// How long an ask in an isolate without counts waits for the first read
// before it goes on with none: a slow or failed count never fails the ask. An
// isolate with counts, even stale ones, never waits. See Design > Exchange,
// "Refunds and ratings".
export const RATING_COUNTS_TIMEOUT_MS = 1000;

// The dispatched events a hub's p50Ms and p95Ms cover on the Curator API: its
// newest LATENCY_SAMPLES_PER_HUB within LATENCY_WINDOW_MS, so the read is
// bounded by both, not by every event ever recorded. See Design > Exchange,
// "Curator API".
export const LATENCY_WINDOW_MS = 7 * 24 * 60 * 60_000;
export const LATENCY_SAMPLES_PER_HUB = 1000;

// Router request settings. GPT-6 Luna rejects max_tokens.
export const ROUTER_MAX_COMPLETION_TOKENS = 400;

// The router model's listed price, in US dollars per million tokens, which
// a routing check prices its calls at: GPT-6 Luna, $0.10 input and $0.50
// output (experiments/clairic/decline-check/rule.ts, PRICE_PER_MILLION, from
// OpenRouter's models endpoint on 2026-10-22). Cached prompt tokens are
// priced as input, so a check never reads cheaper than it was. A call the
// gateway's cache answered cost nothing; one whose usage didn't come back
// is priced at ROUTER_WORST_CALL_TOKENS, above every call the decline check
// observed. See Design > Hub agent, "The route check is the HubRouter's own".
export const ROUTER_PRICE_USD_PER_MILLION = { input: 0.1, output: 0.5 } as const;
export const ROUTER_WORST_CALL_TOKENS = { input: 3000, output: ROUTER_MAX_COMPLETION_TOKENS } as const;

// How long the AI Gateway keeps a router response for an identical request.
// The request includes the hub's prompt and tools, so a manifest change
// misses the cache. The gateway's own default is 300 seconds.
export const ROUTER_CACHE_TTL_SECONDS = 3600;

// Direct Thing fetches: refs per request, and the repositories they may read.
export const THINGS_MAX_REFS = 50;

// Shadow mode: how far the index's top intent must lead the next candidate,
// in vector distance, before verified codes could skip intent routing.
export const FAST_PATH_MARGIN = 0.05;

// The span extractor, which the fast-path twin calls through the AI binding
// on Workers AI directly, not through the AI Gateway, as round six measured
// it: p50 173 ms, p99 312 ms over 1,155 calls in a deployed Worker
// (experiments/clairic/results.md, "Round six results on F"). The timeout is
// about ten times that p99. See Design > HubRouter, "Digital twins".
export const SPAN_EXTRACTOR_MODEL = "@cf/cloudflare/clairic";
export const SPAN_EXTRACTOR_TIMEOUT_MS = 3000;
// The doubt rule's confidence floor, chosen on generated question sets in
// rounds three and four; the twin records the raw confidences so it can be
// tuned from production.
export const DOUBT_CONFIDENCE = 0.6;

// The fast path's intent pick: one Jev call with Stage 1's chosen
// configuration (experiments/clairic/results.md, "Stage 1's readout"), a
// Noul on whether the question asks for one of the hub's candidate intents
// and a Choice over them. It picks the Choice's intent when the Noul reaches
// INTENT_PICK_ANSWERABLE and the intent's probability INTENT_PICK_CONFIDENCE.
// See Design > HubRouter, "The fast path".
export const INTENT_PICK_ANSWERABLE = 0.5;
export const INTENT_PICK_CONFIDENCE = 0.5;
// Below this Noul the fast path declines, as intent routing declines. Chosen
// from data, not tested on held-out questions: in the decline check
// (experiments/clairic/decline-check/ledger.jsonl, every intent listed), every
// notInScope question had at most 0.15 (252 of 252 repeats) and every
// in-scope one reached 0.62 (1,350 of 1,350); in Stage 1, with the index's
// candidate intents as served, 3 of 1,218 in-scope repeats fell under it.
export const FAST_PATH_DECLINE_BELOW = 0.2;
// Jev's System One answered the decline check's requests from a Mac in p50
// 80 ms, p99 240 ms and at most 394 ms over 2,250 calls: indicative, not a
// deployed latency. The HubSelector's own Jev call has the same timeout.
export const INTENT_PICK_TIMEOUT_MS = 1000;
// The intent pick's own model setting, so a change to hub scoring's
// HUB_SCORE_MODEL doesn't change the configuration Stage 1 chose.
export const INTENT_PICK_MODEL = "jev-latest";
// The choice fallback: on a hub the fast path serves, when resolution maps a
// choice value to none of its keys, one Jev Choice over the keys picks one,
// which is used only at this probability or above. Chosen, not measured: no question set
// has been run through the fallback. The call has INTENT_PICK_TIMEOUT_MS.
export const CHOICE_FALLBACK_CONFIDENCE = 0.8;

// Embedding model for the intent index.
export const INDEX_EMBEDDING_MODEL = "voyage/voyage-4-lite";
export const INDEX_EMBEDDING_DIMS = 1024;

// One dollar per million answers, in nanodollars per answer.
export const NANO_PER_USD_PER_MILLION = 1000;

// Bearer tokens on the Consumer API. WarmHub's answer for a token is kept
// for 60 seconds, the revocation lag that WarmHub's own edge accepts, and
// never past the token's expiry. See Design > Exchange, "Bearer tokens".
export const BEARER_CACHE_TTL_MS = 60_000;
export const BEARER_CACHE_MAX_ENTRIES = 10_000;
export const MAX_BEARER_LENGTH = 8192;

// Ledger entries that GET /v1/account returns, newest first.
export const ACCOUNT_LEDGER_ROWS = 50;

// Test runs (see Design > Exchange, "Test runs"): the bid each question is
// asked with, high enough that no hub's ask turns it away, since a test run
// is never charged; and how long a run may stay running before it reads as
// an error.
export const TEST_RUN_BID_USD_PER_MILLION = 1_000_000;
export const TEST_RUN_TIMEOUT_MS = 30 * 60_000;
// How many of a hub's newest staged toolsets keep their intent index rows,
// besides the active one. Staging deletes the rest. See Design > Operations,
// "Intent index rebuild".
export const INDEXED_STAGED_TOOLSETS = 5;
// How many times the HUB_JOBS queue delivers a hub's step before the ledger
// records it as failed: the consumer's max_retries in wrangler.jsonc, plus the
// first delivery. test/hub-jobs.test.ts checks the two agree. See Design >
// Operations, "Hub jobs".
export const HUB_JOB_MAX_ATTEMPTS = 5;

// How many test runs a hub may start in an hour, of any kind or commit. Each
// question costs an embedding, hub scoring, a router call and a tool run, and
// none is charged. See Design > Exchange, "Test runs of a toolset".
export const TEST_RUNS_PER_HUB_PER_HOUR = 40;
